<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import store from '@/store';
import { useWindowSize } from '@/utils/composables/useWindowSize.js';
import messages, { MSG_BUTTON, MSG_TITLE, MSG_TYPE } from '@/utils/messages';
import helpers from '@/utils/helpers';
import MNG_AuthorApp002_Right_Store from '@/views/move/mng_author_app/MNG_AuthorApp002_Right/store';
import mode_switch from '@/views/move/mng_author_app/MNG_AuthorApp003_Right/template_mode/index.vue';
import template from './template.html';
import './style.scss';

export default {
    name: 'MNG_AuthorApp002_Right',
    emits: ['new-form', 'close-right', 'saved'],
    setup() {
        const { windowWidth } = useWindowSize();
        return { windowWidth };
    },
    props: {
        typeNo: {
            type: [String, Number],
            required: true
        },
        formNo: {
            type: [String, Number],
            default: ''
        },
        initialData: {
            type: Object,
            default: () => ({})
        },
        excludedTypeCodes: {
            type: Array,
            default: () => []
        },
        popupMode: {
            type: Boolean,
            default: false
        }
    },
    template: template,
    components: {
        mode_switch
    },
    beforeCreate() {
        if (!store.hasModule('MNG_AuthorApp002_Right_Store')) {
            store.registerModule('MNG_AuthorApp002_Right_Store', MNG_AuthorApp002_Right_Store);
        }
    },
    async created() {
        this.resetDataInit();
        if (!this.popupMode) {
            this.setScreenId(this.screenId);
            this.setHeader(this.header);
        }
        await this.initCompany();
        await this.loadStatusOptions();
    },
    mounted() {
        this.syncInput();
    },
    watch: {
        formNo() {
            this.syncInput();
        },
        typeNo() {
            this.syncInput();
        },
        initialData: {
            deep: true,
            handler() {
                this.syncInput();
            }
        },
        'data.typeNo'() {
            if (!this.popupMode) {
                this.setHeader(this.header);
            }
        },
        'data.application_no'() {
            if (!this.popupMode) {
                this.setHeader(this.header);
            }
        },
        'data.status_code'() {
            if (!this.popupMode) {
                this.setHeader(this.header);
            }
        }
    },
    unmounted() {
        this.hideModalMessage();
    },
    data() {
        return {
            screenId: 'MNG_AuthorApp002',
            userInfo: JSON.parse(localStorage.getItem('userInfo') || '{}'),
            lastSyncSignature: '',
            lastLoadedDetailSignature: '',
            lastStatusOptionCompanyCd: ''
        };
    },
    computed: {
        ...mapState('app', ['user']),
        ...mapState('MNG_AuthorApp002_Right_Store', ['data', 'searchData', 'options']),
        loginMdCd() {
            return this.userInfo?.md_cd ?? this.user?.md_cd ?? this.userInfo?.MDCD ?? this.user?.MDCD ?? '';
        },
        filteredTypeOptions() {
            const excludedCodes = this.excludedTypeCodes.map((item) => Number(item));
            if (excludedCodes.length === 0) {
                return this.options.types || [];
            }
            return (this.options.types || []).filter((item) => !excludedCodes.includes(Number(item.code)));
        },
        displayStatus() {
            if (!this.data.application_no) {
                return '';
            }
            return getOptionLabel(this.options.statuses, this.data.status_code) || this.data.status || '';
        },
        isStatusTwo() {
            return Number(this.data.status_code) === 2;
        },
        isRegisterDisabled() {
            return !this.data.typeNo || this.isStatusTwo || this.data.status_code == 5;
        },
        isCancelDisabled() {
            return !this.data.application_no || this.isStatusTwo || this.data.status_code == 5;
        },
        popupActionButtons() {
            return [
                {
                    title: { text: '新規登録', class: {}, style: {} },
                    icon: { name: 'fa-solid fa-plus-circle', class: {}, style: {} },
                    isShow: true,
                    isDisabled: false,
                    onClick: () => {
                        this.resetPopupForm();
                    }
                },
                {
                    title: { text: '登録', class: {}, style: {} },
                    icon: { name: 'fa-solid fa-check', class: {}, style: {} },
                    isShow: true,
                    isDisabled: this.isRegisterDisabled,
                    onClick: () => {
                        this.saveData();
                    }
                },
                {
                    title: { text: '申請取消', class: {}, style: {} },
                    icon: { name: 'fa-solid fa-ban', class: {}, style: {} },
                    isShow: true,
                    isDisabled: this.isCancelDisabled,
                    onClick: () => {
                        this.cancelRequest();
                    }
                },
                {
                    title: { text: '戻る', class: {}, style: {} },
                    icon: { name: 'fa-solid fa-arrow-left', class: {}, style: {} },
                    isShow: true,
                    isDisabled: false,
                    onClick: () => {
                        this.$emit('close-right');
                    }
                }
            ];
        },
        header() {
            return {
                title: '申請一覧',
                topButtons: [
                    {
                        title: { text: '新規登録', class: {}, style: {} },
                        icon: { name: 'fa-solid fa-plus-circle', class: {}, style: {} },
                        isShow: true,
                        isDisabled: false,
                        onClick: () => {
                            this.resetDataInit();
                            this.initCompany();
                            this.data.typeNo = '';
                            this.data.application_no = '';
                            if (this.$store.state.DetailStore?.data) {
                                this.$store.commit('DetailStore/resetData');
                            }
                            this.$emit('new-form');
                        }
                    },
                    {
                        title: { text: '登録', class: {}, style: {} },
                        icon: { name: 'fa-solid fa-check', class: {}, style: {} },
                        isShow: true,
                        isDisabled: this.isRegisterDisabled,
                        onClick: () => {
                            this.saveData();
                        }
                    },
                    {
                        title: { text: '申請取消', class: {}, style: {} },
                        icon: { name: 'fa-solid fa-ban', class: {}, style: {} },
                        isShow: true,
                        isDisabled: this.isCancelDisabled,
                        onClick: () => {
                            this.cancelRequest();
                        }
                    },
                    {
                        title: { text: '戻る', class: {}, style: {} },
                        icon: { name: 'fa-solid fa-arrow-left', class: {}, style: {} },
                        isShow: true,
                        isDisabled: false,
                        onClick: () => {
                            this.$emit('close-right');
                        }
                    }
                ]
            };
        },
        dummyData() {
            return this.searchData || [];
        }
    },
    methods: {
        ...mapMutations('app', ['showModalMessage', 'hideModalMessage', 'setScreenId', 'setHeader']),
        ...mapMutations('MNG_AuthorApp002_Right_Store', ['resetDataInit', 'setData', 'setTypes', 'setStatuses', 'setSearchData']),
        ...mapActions('MNG_AuthorApp002_Right_Store', ['getData', 'storeSave', 'storeCancel']),
        async initCompany() {
            const loginCompanyCd = this.userInfo?.company_cd ?? this.user?.company_cd ?? '';
            this.data.company_cd = loginCompanyCd ? String(loginCompanyCd) : '';
            this.data.md_cd = this.loginMdCd ? String(this.loginMdCd) : '';
        },
        resetPopupForm() {
            this.resetDataInit();
            this.initCompany();
            if (this.$store.state.DetailStore?.data) {
                this.$store.commit('DetailStore/resetData');
            }
            this.applyInitialData();
        },
        handleTypeNoChange(e, nextTypeNo) {
            const defaultTargetMonth = Number(nextTypeNo) === 11 ? getCurrentMonthYear() : '';
            const preservedData = {
                company_cd: this.data.company_cd || '',
                company_name: this.data.company_name || '',
                md_cd: this.data.md_cd || '',
                application_no: this.data.application_no || '',
                typeNo: nextTypeNo || '',
                target_month: defaultTargetMonth
            };

            this.resetDataInit();
            this.setData(preservedData);
            this.lastLoadedDetailSignature = '';

            if (this.$store.state.DetailStore?.data) {
                this.$store.commit('DetailStore/resetData');
            }
        },
        syncInput() {
            const nextApplicationNo = this.formNo || '';
            const nextTypeNo = this.typeNo || '';
            const nextSignature = `${nextApplicationNo}::${nextTypeNo}`;
            if (this.lastSyncSignature === nextSignature) {
                return;
            }
            this.lastSyncSignature = nextSignature;

            this.data.application_no = nextApplicationNo;
            this.data.typeNo = nextTypeNo;
            if (this.data.application_no) {
                this.loadDetail();
                return;
            }
            this.lastLoadedDetailSignature = '';
            if (this.$store.state.DetailStore?.data) {
                this.$store.commit('DetailStore/resetData');
            }
            this.applyInitialData();
        },
        applyInitialData() {
            if (!this.initialData || Object.keys(this.initialData).length === 0) {
                this.setData({
                    typeNo: this.typeNo || this.data.typeNo || ''
                });
                return;
            }
            this.setData({
                ...this.initialData,
                typeNo: this.typeNo || this.initialData.typeNo || this.data.typeNo || '',
                application_no: this.formNo || this.initialData.application_no || ''
            });
        },
        loadDetail() {
            const detailSignature = `${this.data.application_no || ''}::${this.data.typeNo || ''}`;
            if (this.lastLoadedDetailSignature === detailSignature) {
                return;
            }
            this.lastLoadedDetailSignature = detailSignature;
            this.getData({
                body: {
                    application_no: this.data.application_no,
                    company_cd: this.data.company_cd || this.user.company_cd,
                    md_cd: this.data.md_cd || this.loginMdCd || ''
                },
                callback: () => {
                    if (this.$store.state.DetailStore?.data !== undefined) {
                        this.$store.commit('DetailStore/setData', {
                            application_no: this.data.application_no || '',
                            application_date: this.data.application_date || '',
                            target_no: this.data.target_no || '',
                            start_date: this.data.start_date || '',
                            end_date: this.data.end_date || '',
                            start_time: this.data.start_time || '',
                            end_time: this.data.end_time || '',
                            time_in: this.data.time_in || '',
                            time_out: this.data.time_out || '',
                            reasonClass: this.data.reasonClass || this.data.reason_class || '',
                            reason: this.data.reason || '',
                            content: this.data.content || '',
                            detailClass: this.data.detailClass || this.data.detail_class || '',
                            ShinseiDetail: this.data.ShinseiDetail || this.data.shinsei_detail || '',
                            money: this.data.money || '',
                            detail: this.data.detail || '',
                            target_month: this.data.target_month || '',
                            file_list: normalizeFileList(this.data.file_list),
                            ...pickDayFlags(this.data)
                        });
                    }
                }
            });
        },
        buildPayload() {
            const detailData = this.$store.state.DetailStore?.data || {};
            const fileList = normalizeFileList(detailData.file_list?.length ? detailData.file_list : this.data.file_list);
            const formData = new FormData();
            const normalizedMonth = normalizeMonthNumber(
                firstFilled(detailData.target_month, this.data.target_month),
                firstFilled(detailData.application_date, this.data.application_date)
            );
            const normalizedArrival = normalizeTimeNumber(
                firstFilled(detailData.time_in, detailData.start_time, this.data.time_in, this.data.start_time)
            );
            const normalizedDeparture = normalizeTimeNumber(
                firstFilled(detailData.time_out, detailData.end_time, this.data.time_out, this.data.end_time)
            );
            const body = {
                ...this.data,
                ...detailData,
                application_no: this.data.application_no || '',
                typeNo: this.data.typeNo || '',
                application_date: combineDateAndTime(firstFilled(detailData.application_date, this.data.application_date), normalizedArrival),
                reason: detailData.reason ?? this.data.reason ?? '',
                reason_class: detailData.reasonClass ?? this.data.reasonClass ?? this.data.reason_class ?? '',
                content: firstFilled(detailData.content, this.data.content),
                detailClass: firstFilled(detailData.detailClass, this.data.detailClass, this.data.detail_class),
                ShinseiDetail: detailData.ShinseiDetail ?? detailData.shinsei_detail ?? this.data.ShinseiDetail ?? this.data.shinsei_detail ?? '',
                time_in: normalizedArrival,
                time_out: normalizedDeparture,
                start_time: normalizedArrival,
                end_time: normalizedDeparture,
                target_month: normalizedMonth,
                company_cd: this.data.company_cd || this.user.company_cd,
                md_cd: this.data.md_cd || this.loginMdCd || ''
            };
            fileList.forEach((item, index) => {
                const slot = index + 1;
                body[`attachment0${slot}`] = item?.path || '';
                body[`old_attachment0${slot}`] = item?.old_path || '';
            });
            Object.keys(body).forEach((key) => {
                if (key === 'file' || key === 'file_list') return;
                formData.append(key, body[key] ?? '');
            });
            fileList.forEach((item, index) => {
                if (item?.file instanceof File) {
                    formData.append('files[]', item.file, item.file.name);
                    formData.append('file_slots[]', `${index + 1}`);
                }
            });
            return formData;
        },
        saveData() {
            if (this.isRegisterDisabled) return;
            this.showModalMessage({
                type: MSG_TYPE.CONFIRM,
                title: MSG_TITLE.C002,
                content: messages.C002,
                cancelText: MSG_BUTTON.cancelText,
                okText: MSG_BUTTON.okText,
                callback: (ok) => {
                    if (!ok) return;
                    this.storeSave({
                        body: this.buildPayload(),
                        callback: (res) => {
                            this.showModalMessage({
                                type: MSG_TYPE.SUCCESS,
                                title: MSG_TITLE.S002,
                                content: messages.S002,
                                okText: MSG_BUTTON.okText,
                                callback: () => {
                                    const detailData = this.$store.state.DetailStore?.data || {};
                                    if (res?.Data?.application_no) {
                                        this.setData({ application_no: res.Data.application_no });
                                    }
                                    if (this.popupMode) {
                                        this.$emit('saved', {
                                            application_no: res?.Data?.application_no || '',
                                            typeNo: this.data.typeNo || '',
                                            data: {
                                                ...this.data,
                                                ...detailData,
                                                application_no: res?.Data?.application_no || this.data.application_no || ''
                                            }
                                        });
                                        return;
                                    }
                                    this.$emit('close-right');
                                }
                            });
                        },
                        onError: (err) => {
                            this.showModalMessage({
                                type: MSG_TYPE.ERROR,
                                title: MSG_TITLE.E001,
                                content: err
                            });
                        }
                    });
                }
            });
        },
        cancelRequest() {
            if (this.isCancelDisabled) return;
            this.showModalMessage({
                type: MSG_TYPE.CONFIRM,
                title: MSG_TITLE.C003,
                content: messages.C003,
                cancelText: MSG_BUTTON.cancelText,
                okText: MSG_BUTTON.okText,
                callback: (ok) => {
                    if (!ok) return;
                    this.storeCancel({
                        body: {
                            application_no: this.data.application_no,
                            company_cd: this.data.company_cd || this.user.company_cd,
                            md_cd: this.data.md_cd || this.loginMdCd || ''
                        },
                        callback: () => {
                            this.showModalMessage({
                                type: MSG_TYPE.SUCCESS,
                                title: MSG_TITLE.S002,
                                content: messages.S002
                            });
                            this.loadDetail();
                        },
                        onError: (err) => {
                            this.showModalMessage({
                                type: MSG_TYPE.ERROR,
                                title: MSG_TITLE.E001,
                                content: err
                            });
                        }
                    });
                }
            });
        },
        changeFile(event, item) {
            const file = event.target.files[0];
            if (!file) return;
            if (helpers.checkOverMaxFileSize(file)) {
                document.querySelector('#reporting_file').value = '';
                return;
            }
            if (file.name.length > 200) {
                this.showModalMessage({
                    type: MSG_TYPE.ERROR,
                    title: MSG_TITLE.E001,
                    content: messages.E007,
                    cancelText: MSG_BUTTON.cancelText,
                    okText: MSG_BUTTON.okText,
                    callback: (ok) => {
                        if (ok) {
                            item.filename = '';
                            item.file = '';
                        }
                    }
                });
                return;
            }
            item.filename = file.name;
            item.file = file;
        },
        deleteFile(item) {
            item.filename = '';
            item.file = '';
            item.file_path = '';
        },
        openFile() {
            document.querySelector('#reporting_file').value = '';
            document.querySelector('#reporting_file').click();
        },
        async loadStatusOptions() {
            try {
                const companyCd = String(this.data.company_cd ?? '').trim();
                if (!companyCd) {
                    this.lastStatusOptionCompanyCd = '';
                    this.setTypes([]);
                    this.setStatuses([]);
                    return;
                }
                if (this.lastStatusOptionCompanyCd === companyCd) {
                    return;
                }
                this.lastStatusOptionCompanyCd = companyCd;
                const result = await helpers.getDataSys005({ meisho_cd: '65,27', company_cd: companyCd });
                const typeList = [];
                const statusList = [];
                (result || []).forEach((item) => {
                    const option = {
                        code: Number(item.code ?? 0),
                        name: item.name ?? ''
                    };
                    if (Number(item.name_cd) === 27) {
                        typeList.push(option);
                    }
                    if (Number(item.name_cd) === 65) {
                        statusList.push(option);
                    }
                });
                this.setTypes(typeList.length > 0 ? typeList : this.options.types);
                this.setStatuses(statusList.length > 0 ? statusList : this.options.statuses);
            } catch (e) {
                this.lastStatusOptionCompanyCd = '';
                console.log('loadStatusOptions: ' + e.message);
            }
        }
    }
};

function normalizeTimeNumber(value) {
    if (!value) return 0;
    return Number(String(value).replace(/\D/g, '')) || 0;
}

function normalizeMonthNumber(value, baseDateValue = '') {
    if (!value) return 0;
    const text = String(value).trim();
    const digits = text.replace(/\D/g, '');
    if (digits.length >= 6) {
        return Number(digits.slice(0, 6)) || 0;
    }
    if (digits.length >= 1 && digits.length <= 2) {
        const month = Number(digits);
        if (month < 1 || month > 12) return 0;
        const year = resolveYear(baseDateValue);
        return Number(`${year}${String(month).padStart(2, '0')}`) || 0;
    }
    return 0;
}

function combineDateAndTime(dateValue, hhmmNumber) {
    if (!dateValue) return '';
    const dateText = String(dateValue).trim().replace(/-/g, '/');
    const baseDate = dateText.substring(0, 10);
    if (!hhmmNumber) return baseDate;
    const padded = String(hhmmNumber).padStart(4, '0');
    const hour = padded.slice(0, 2);
    const minute = padded.slice(2, 4);
    return `${baseDate} ${hour}:${minute}:00`;
}

function pickDayFlags(data) {
    const result = {};
    for (let day = 1; day <= 31; day++) {
        const key = `d${day}`;
        result[key] = Number(data?.[key] ?? data?.[`D${day}`] ?? 0) || 0;
    }
    return result;
}

function firstFilled(...values) {
    for (const value of values) {
        if (value !== null && value !== undefined && `${value}` !== '') {
            return value;
        }
    }
    return '';
}

function resolveYear(value) {
    const date = value ? new Date(value) : new Date();
    if (Number.isNaN(date.getTime())) return new Date().getFullYear();
    return date.getFullYear();
}

function getOptionLabel(options, code) {
    const optionList = options ?? [];
    const match = optionList.find((item) => `${item.code ?? ''}` === `${code ?? ''}`);
    return match?.name ?? '';
}

function normalizeFileList(list = []) {
    return Array.from({ length: 5 }, (_, index) => {
        const item = list?.[index] || {};
        const path = item?.path || '';
        const oldPath = item?.old_path !== undefined ? item.old_path || '' : path;
        return {
            name: item?.name !== undefined ? item.name || '' : extractFileName(path || oldPath),
            path,
            old_path: oldPath,
            file: item?.file ?? null
        };
    });
}

function extractFileName(path) {
    if (!path) return '';
    const parts = `${path}`.split(/[\\/]+/);
    return parts[parts.length - 1] ?? '';
}

function getCurrentMonthYear() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}/${month}`;
}
</script>
